import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/react";
import jsPDF from "jspdf";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Clock,
  Search,
  CheckCircle2,
  XCircle,
  StickyNote,
  Send,
  Pencil,
  Trash2,
  X,
  Check,
  FileDown,
  Mail,
  Printer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import AdminLayout, { AdminPageHeader } from "./layout";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface SafetyReport {
  id: number;
  referenceNumber: string;
  userId: string;
  submitterRole: string | null;
  concernType: string;
  description: string;
  pageOrFeature: string | null;
  status: string;
  createdAt: string;
}

interface SafetyReportNote {
  id: number;
  safetyReportId: number;
  actorUserId: string;
  actorDisplayName: string;
  note: string;
  createdAt: string;
}

interface SafetyReportStatusHistoryEntry {
  id: number;
  safetyReportId: number;
  actorUserId: string;
  actorDisplayName: string;
  previousStatus: string;
  newStatus: string;
  createdAt: string;
}

type ConcernType = "clinical-content-error" | "ai-response-concern" | "data-issue" | "other" | "all";
type StatusFilter = "pending" | "investigating" | "resolved" | "closed" | "all";
type RoleFilter = "nurse" | "doctor" | "pharmacist" | "physio" | "ot" | "all";
type ReportStatus = "pending" | "investigating" | "resolved" | "closed";

const ROLE_LABELS: Record<string, string> = {
  nurse: "Nurse",
  doctor: "Doctor",
  pharmacist: "Pharmacist",
  physio: "Physiotherapist",
  ot: "Occupational Therapist",
};

const ROLE_COLOURS: Record<string, string> = {
  nurse: "bg-teal-100 text-teal-800 border-teal-200",
  doctor: "bg-blue-100 text-blue-800 border-blue-200",
  pharmacist: "bg-violet-100 text-violet-800 border-violet-200",
  physio: "bg-orange-100 text-orange-800 border-orange-200",
  ot: "bg-pink-100 text-pink-800 border-pink-200",
};

const CONCERN_LABELS: Record<string, string> = {
  "clinical-content-error": "Clinical content error",
  "ai-response-concern": "AI response concern",
  "data-issue": "Data issue",
  "other": "Other",
};

const CONCERN_COLOURS: Record<string, string> = {
  "clinical-content-error": "bg-red-100 text-red-800 border-red-200",
  "ai-response-concern": "bg-purple-100 text-purple-800 border-purple-200",
  "data-issue": "bg-amber-100 text-amber-800 border-amber-200",
  "other": "bg-slate-100 text-slate-700 border-slate-200",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  investigating: "Investigating",
  resolved: "Resolved",
  closed: "Closed",
};

const STATUS_COLOURS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  investigating: "bg-blue-100 text-blue-800 border-blue-200",
  resolved: "bg-green-100 text-green-800 border-green-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_FLOW: ReportStatus[] = ["pending", "investigating", "resolved", "closed"];

async function apiFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${basePath}${path}`, { credentials: "include", ...opts });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error((b as { error?: string }).error ?? "Request failed");
  }
  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined;
  }
  return res.json() as Promise<unknown>;
}

function createDocRenderer(doc: jsPDF) {
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const ml = 15;
  const mr = 15;
  const maxW = pw - ml - mr;
  let y = 0;

  const TEAL: [number, number, number] = [13, 103, 96];
  const SLATE_DARK: [number, number, number] = [30, 41, 59];
  const SLATE_MID: [number, number, number] = [71, 85, 105];
  const SLATE_LIGHT: [number, number, number] = [148, 163, 184];
  const BLUE_BG: [number, number, number] = [239, 246, 255];
  const LINE_COLOR: [number, number, number] = [203, 213, 225];

  const ensureSpace = (needed: number) => {
    if (y + needed > ph - 15) {
      doc.addPage();
      y = 20;
    }
  };

  const addWrappedText = (
    text: string,
    x: number,
    width: number,
    fontSize: number,
    style: "normal" | "bold" | "italic",
    color: [number, number, number],
    lineSpacing = 1.4,
  ): number => {
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", style);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, width) as string[];
    const lineH = (fontSize / 72) * 25.4 * lineSpacing;
    ensureSpace(lines.length * lineH + 2);
    doc.text(lines, x, y);
    y += lines.length * lineH;
    return lines.length;
  };

  const addSeparator = () => {
    ensureSpace(6);
    doc.setDrawColor(...LINE_COLOR);
    doc.setLineWidth(0.3);
    doc.line(ml, y, pw - mr, y);
    y += 5;
  };

  const labelValue = (label: string, value: string) => {
    ensureSpace(8);
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...SLATE_MID);
    doc.text(label + ":", ml, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...SLATE_DARK);
    const lines = doc.splitTextToSize(value, maxW - 52) as string[];
    doc.text(lines, ml + 52, y);
    y += Math.max(lines.length, 1) * 5 + 1;
  };

  const renderPageBanner = () => {
    doc.setFillColor(...TEAL);
    doc.rect(0, 0, pw, 16, "F");
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("RheumHub — Safety Report", ml, 10);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("DCB0129 Clinical Safety Incident Record", pw - mr, 10, { align: "right" });
    y = 24;
  };

  const renderReport = (
    report: SafetyReport,
    notes: SafetyReportNote[],
    statusHistory: SafetyReportStatusHistoryEntry[],
  ) => {
    addWrappedText(report.referenceNumber, ml, maxW, 15, "bold", SLATE_DARK);
    y += 1;
    addWrappedText(`Generated: ${new Date().toLocaleString("en-GB")}`, ml, maxW, 8, "italic", SLATE_LIGHT);
    y += 4;
    addSeparator();

    addWrappedText("Report Details", ml, maxW, 11, "bold", SLATE_DARK);
    y += 3;
    labelValue("Reference Number", report.referenceNumber);
    labelValue("Concern Type", CONCERN_LABELS[report.concernType] ?? report.concernType);
    labelValue(
      "Submitter Role",
      report.submitterRole ? (ROLE_LABELS[report.submitterRole] ?? report.submitterRole) : "Unknown",
    );
    labelValue("Submitter User ID", report.userId);
    labelValue("Page / Feature", report.pageOrFeature ?? "Not specified");
    labelValue("Submitted", new Date(report.createdAt).toLocaleString("en-GB"));
    labelValue("Current Status", STATUS_LABELS[report.status] ?? report.status);
    y += 3;
    addSeparator();

    addWrappedText("Full Description", ml, maxW, 11, "bold", SLATE_DARK);
    y += 3;
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(...LINE_COLOR);
    doc.setLineWidth(0.3);
    const descLines = doc.splitTextToSize(report.description, maxW - 8) as string[];
    const descLineH = (10 / 72) * 25.4 * 1.4;
    const descBlockH = descLines.length * descLineH + 8;
    ensureSpace(descBlockH);
    doc.roundedRect(ml, y - 2, maxW, descBlockH, 2, 2, "FD");
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...SLATE_DARK);
    doc.text(descLines, ml + 4, y + 4);
    y += descBlockH + 6;
    addSeparator();

    addWrappedText(
      `Status History (${statusHistory.length} transition${statusHistory.length !== 1 ? "s" : ""})`,
      ml, maxW, 11, "bold", SLATE_DARK,
    );
    y += 3;
    if (statusHistory.length === 0) {
      addWrappedText(
        "No status transitions recorded — report has not been moved from its initial status.",
        ml, maxW, 10, "italic", SLATE_LIGHT,
      );
    } else {
      for (let i = 0; i < statusHistory.length; i++) {
        const entry = statusHistory[i];
        const rowH = 12;
        ensureSpace(rowH + 3);
        doc.setFillColor(i % 2 === 0 ? 248 : 241, i % 2 === 0 ? 250 : 245, i % 2 === 0 ? 252 : 249);
        doc.setDrawColor(...LINE_COLOR);
        doc.setLineWidth(0.2);
        doc.rect(ml, y - 2, maxW, rowH, "FD");
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...SLATE_MID);
        doc.text(`#${i + 1}`, ml + 2, y + 4);
        const prevLabel = STATUS_LABELS[entry.previousStatus] ?? entry.previousStatus;
        const newLabel = STATUS_LABELS[entry.newStatus] ?? entry.newStatus;
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...SLATE_DARK);
        doc.text(`${prevLabel}  →  ${newLabel}`, ml + 12, y + 4);
        doc.setFontSize(7.5);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(...SLATE_MID);
        doc.text(`by ${entry.actorDisplayName} (${entry.actorUserId})`, ml + 12, y + 9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...SLATE_LIGHT);
        doc.text(new Date(entry.createdAt).toLocaleString("en-GB"), pw - mr - 2, y + 4, { align: "right" });
        y += rowH + 2;
      }
    }
    y += 3;
    addSeparator();

    addWrappedText(`Investigation Notes (${notes.length})`, ml, maxW, 11, "bold", SLATE_DARK);
    y += 3;
    if (notes.length === 0) {
      addWrappedText("No investigation notes recorded.", ml, maxW, 10, "italic", SLATE_LIGHT);
    } else {
      for (const note of notes) {
        const noteBodyLines = doc.splitTextToSize(note.note, maxW - 8) as string[];
        const noteLineH = (10 / 72) * 25.4 * 1.4;
        const noteBlockH = noteBodyLines.length * noteLineH + 16;
        ensureSpace(noteBlockH + 4);
        doc.setFillColor(...BLUE_BG);
        doc.setDrawColor(186, 230, 253);
        doc.setLineWidth(0.3);
        doc.roundedRect(ml, y - 2, maxW, noteBlockH, 2, 2, "FD");
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...SLATE_MID);
        doc.text(note.actorDisplayName, ml + 4, y + 5);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...SLATE_LIGHT);
        doc.text(`User ID: ${note.actorUserId}`, ml + 4, y + 10);
        doc.text(new Date(note.createdAt).toLocaleString("en-GB"), pw - mr - 2, y + 5, { align: "right" });
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...SLATE_DARK);
        doc.text(noteBodyLines, ml + 4, y + 16);
        y += noteBlockH + 5;
      }
    }
    y += 4;
    addSeparator();

    ensureSpace(8);
    doc.setFontSize(7);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...SLATE_LIGHT);
    doc.text(
      `Exported from RheumHub on ${new Date().toLocaleString("en-GB")} · Confidential — DCB0129 Clinical Safety Document`,
      ml, y,
    );
  };

  return {
    renderPageBanner,
    renderReport,
    setY: (val: number) => { y = val; },
    pw,
    ph,
    ml,
    mr,
  };
}

function generateSafetyReportPdf(
  report: SafetyReport,
  notes: SafetyReportNote[],
  statusHistory: SafetyReportStatusHistoryEntry[],
) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const renderer = createDocRenderer(doc);
  renderer.renderPageBanner();
  renderer.renderReport(report, notes, statusHistory);
  doc.save(`safety-report-${report.referenceNumber}.pdf`);
}

function generateSafetyReportPdfBytes(
  report: SafetyReport,
  notes: SafetyReportNote[],
  statusHistory: SafetyReportStatusHistoryEntry[],
): string {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const renderer = createDocRenderer(doc);
  renderer.renderPageBanner();
  renderer.renderReport(report, notes, statusHistory);
  const dataUri = doc.output("datauristring") as string;
  return dataUri.split(",")[1] ?? "";
}

function generateBulkPdf(
  items: Array<{ report: SafetyReport; notes: SafetyReportNote[]; statusHistory: SafetyReportStatusHistoryEntry[] }>,
  filterDescription: string,
) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const ml = 15;
  const mr = 15;
  const TEAL: [number, number, number] = [13, 103, 96];
  const SLATE_DARK: [number, number, number] = [30, 41, 59];
  const SLATE_LIGHT: [number, number, number] = [148, 163, 184];
  const LINE_COLOR: [number, number, number] = [203, 213, 225];

  // ── Cover page ──────────────────────────────────────────────────────────
  doc.setFillColor(...TEAL);
  doc.rect(0, 0, pw, 30, "F");
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("RheumHub — Safety Reports Register", ml, 13);
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.text("DCB0129 Clinical Safety Incident Records — Bulk Export", pw - mr, 13, { align: "right" });
  doc.setFontSize(8);
  doc.text(`Exported: ${new Date().toLocaleString("en-GB")}`, pw - mr, 22, { align: "right" });

  let cy = 52;
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...SLATE_DARK);
  doc.text("Bulk Safety Report Export", ml, cy);
  cy += 12;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...SLATE_DARK);
  doc.text(`${items.length} report${items.length !== 1 ? "s" : ""} included`, ml, cy);
  cy += 8;

  if (filterDescription) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...SLATE_LIGHT);
    doc.text(`Filters: ${filterDescription}`, ml, cy);
    cy += 8;
  }

  cy += 4;
  doc.setDrawColor(...LINE_COLOR);
  doc.setLineWidth(0.3);
  doc.line(ml, cy, pw - mr, cy);
  cy += 8;

  // Table of contents
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...SLATE_DARK);
  doc.text("Contents", ml, cy);
  cy += 6;

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  for (let i = 0; i < items.length; i++) {
    if (cy > ph - 20) break;
    const r = items[i].report;
    doc.setTextColor(...SLATE_DARK);
    doc.text(`${i + 1}.  ${r.referenceNumber}`, ml + 4, cy);
    doc.setTextColor(...SLATE_LIGHT);
    doc.text(
      `${CONCERN_LABELS[r.concernType] ?? r.concernType} · ${STATUS_LABELS[r.status] ?? r.status}`,
      pw - mr - 2, cy, { align: "right" },
    );
    cy += 5.5;
  }

  doc.setFontSize(7);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...SLATE_LIGHT);
  doc.text("Confidential — For authorised RheumHub administrators only", ml, ph - 12);

  // ── One page-group per report ────────────────────────────────────────────
  const renderer = createDocRenderer(doc);
  for (const { report, notes, statusHistory } of items) {
    doc.addPage();
    renderer.setY(0);
    renderer.renderPageBanner();
    renderer.renderReport(report, notes, statusHistory);
  }

  doc.save(`safety-reports-bulk-${new Date().toISOString().split("T")[0]}.pdf`);
}

function exportCsv(reports: SafetyReport[], filterDescription: string) {
  const headers = [
    "Reference Number",
    "Concern Type",
    "Description",
    "Submitted Date",
    "Status",
    "Submitter Role",
    "User ID",
    "Page / Feature",
  ];

  const escape = (val: string) => `"${String(val).replace(/"/g, '""')}"`;

  const rows = reports.map((r) => [
    r.referenceNumber,
    CONCERN_LABELS[r.concernType] ?? r.concernType,
    r.description,
    new Date(r.createdAt).toLocaleString("en-GB"),
    STATUS_LABELS[r.status] ?? r.status,
    r.submitterRole ? (ROLE_LABELS[r.submitterRole] ?? r.submitterRole) : "Unknown",
    r.userId,
    r.pageOrFeature ?? "",
  ]);

  const csvContent = [
    headers.map(escape).join(","),
    ...rows.map((row) => row.map(escape).join(",")),
  ].join("\r\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const tag = filterDescription
    .replace(/[^a-z0-9]/gi, "-")
    .toLowerCase()
    .replace(/-+/g, "-")
    .slice(0, 30);
  a.download = `safety-reports-${new Date().toISOString().split("T")[0]}${tag ? `-${tag}` : ""}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant="outline" className={`text-xs font-medium gap-1 ${STATUS_COLOURS[status] ?? ""}`}>
      {status === "pending" && <Clock className="h-3 w-3" />}
      {status === "investigating" && <Search className="h-3 w-3" />}
      {status === "resolved" && <CheckCircle2 className="h-3 w-3" />}
      {status === "closed" && <XCircle className="h-3 w-3" />}
      {STATUS_LABELS[status] ?? status}
    </Badge>
  );
}

function RoleBadge({ role }: { role: string | null }) {
  if (!role) return <span className="text-xs text-slate-400 italic">Unknown</span>;
  return (
    <Badge variant="outline" className={`text-xs font-medium ${ROLE_COLOURS[role] ?? "bg-slate-100 text-slate-600 border-slate-200"}`}>
      {ROLE_LABELS[role] ?? role}
    </Badge>
  );
}

function NotesSection({ reportId }: { reportId: number }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { user } = useUser();
  const currentUserId = user?.id ?? null;

  const [noteText, setNoteText] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");

  const { data: notes = [], isLoading } = useQuery<SafetyReportNote[]>({
    queryKey: ["admin", "safety-report-notes", reportId],
    queryFn: () => apiFetch(`/api/admin/safety-reports/${reportId}/notes`) as Promise<SafetyReportNote[]>,
  });

  const addNote = useMutation({
    mutationFn: (note: string) =>
      apiFetch(`/api/admin/safety-reports/${reportId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      }),
    onSuccess: () => {
      setNoteText("");
      qc.invalidateQueries({ queryKey: ["admin", "safety-report-notes", reportId] });
      toast({ title: "Note added" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const editNote = useMutation({
    mutationFn: ({ noteId, note }: { noteId: number; note: string }) =>
      apiFetch(`/api/admin/safety-reports/${reportId}/notes/${noteId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      }),
    onSuccess: () => {
      setEditingId(null);
      setEditText("");
      qc.invalidateQueries({ queryKey: ["admin", "safety-report-notes", reportId] });
      toast({ title: "Note updated" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteNote = useMutation({
    mutationFn: (noteId: number) =>
      apiFetch(`/api/admin/safety-reports/${reportId}/notes/${noteId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "safety-report-notes", reportId] });
      toast({ title: "Note deleted" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const handleSubmit = () => {
    const trimmed = noteText.trim();
    if (!trimmed) return;
    addNote.mutate(trimmed);
  };

  const startEdit = (n: SafetyReportNote) => {
    setEditingId(n.id);
    setEditText(n.note);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const confirmEdit = (noteId: number) => {
    const trimmed = editText.trim();
    if (!trimmed) return;
    editNote.mutate({ noteId, note: trimmed });
  };

  return (
    <div className="border-t border-slate-100 pt-4 space-y-3">
      <div className="flex items-center gap-1.5">
        <StickyNote className="h-3.5 w-3.5 text-slate-500" />
        <p className="text-xs font-medium text-slate-600">Investigation notes</p>
        {notes.length > 0 && (
          <span className="text-xs text-slate-400">({notes.length})</span>
        )}
      </div>

      {isLoading ? (
        <p className="text-xs text-slate-400 italic">Loading notes…</p>
      ) : notes.length === 0 ? (
        <p className="text-xs text-slate-400 italic">No notes yet. Add one below to begin logging your investigation.</p>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => {
            const isOwn = currentUserId !== null && n.actorUserId === currentUserId;
            const isEditing = editingId === n.id;

            return (
              <div key={n.id} className="group relative bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5">
                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="text-sm min-h-[72px] resize-none bg-white"
                      maxLength={2000}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                          e.preventDefault();
                          confirmEdit(n.id);
                        }
                        if (e.key === "Escape") cancelEdit();
                      }}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => confirmEdit(n.id)}
                        disabled={!editText.trim() || editNote.isPending}
                        className="gap-1.5 h-7 text-xs"
                      >
                        <Check className="h-3 w-3" />
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={cancelEdit}
                        disabled={editNote.isPending}
                        className="gap-1.5 h-7 text-xs"
                      >
                        <X className="h-3 w-3" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap pr-16">{n.note}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Added by {n.actorDisplayName}
                      {" · "}
                      {new Date(n.createdAt).toLocaleString("en-GB")}
                    </p>
                    {isOwn && (
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => startEdit(n)}
                          title="Edit note"
                          className="p-1 rounded hover:bg-blue-100 text-slate-400 hover:text-slate-700 transition-colors"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => deleteNote.mutate(n.id)}
                          title="Delete note"
                          disabled={deleteNote.isPending}
                          className="p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex gap-2 items-end">
        <Textarea
          placeholder="Add an investigation note, e.g. 'Confirmed with clinical lead, content corrected'…"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          className="text-sm min-h-[72px] resize-none"
          maxLength={2000}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!noteText.trim() || addNote.isPending}
          className="gap-1.5 shrink-0"
        >
          <Send className="h-3.5 w-3.5" />
          Add note
        </Button>
      </div>
      <p className="text-xs text-slate-400">Press Ctrl+Enter to submit</p>
    </div>
  );
}

function EmailReportDialog({
  report,
  open,
  onOpenChange,
}: {
  report: SafetyReport;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [recipientEmail, setRecipientEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  const handleSend = async () => {
    const trimmedEmail = recipientEmail.trim();
    if (!trimmedEmail) return;

    setIsSending(true);
    try {
      const [notes, statusHistory] = await Promise.all([
        apiFetch(`/api/admin/safety-reports/${report.id}/notes`) as Promise<SafetyReportNote[]>,
        apiFetch(`/api/admin/safety-reports/${report.id}/status-history`) as Promise<SafetyReportStatusHistoryEntry[]>,
      ]);

      const pdfBase64 = generateSafetyReportPdfBytes(report, notes ?? [], statusHistory ?? []);

      await apiFetch(`/api/admin/safety-reports/${report.id}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipientEmail: trimmedEmail, message: message.trim(), pdfBase64 }),
      });

      qc.invalidateQueries({ queryKey: ["admin", "safety-report-notes", report.id] });
      toast({ title: "Report sent", description: `Safety report emailed to ${trimmedEmail}.` });
      setRecipientEmail("");
      setMessage("");
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Failed to send",
        description: e instanceof Error ? e.message : "Could not send the report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Email safety report</DialogTitle>
          <DialogDescription>
            Send <span className="font-mono text-xs font-semibold text-slate-700">{report.referenceNumber}</span> as a
            PDF attachment to a clinical lead or regulator.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="recipient-email">Recipient email address</Label>
            <Input
              id="recipient-email"
              type="email"
              placeholder="clinicallead@hospital.nhs.uk"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              disabled={isSending}
              onKeyDown={(e) => {
                if (e.key === "Enter" && recipientEmail.trim()) handleSend();
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email-message">
              Message <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Textarea
              id="email-message"
              placeholder="Add a short covering message to the recipient…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isSending}
              className="min-h-[80px] resize-none text-sm"
              maxLength={2000}
            />
          </div>
          <p className="text-xs text-slate-400">
            The PDF will be generated from the current report data and attached automatically. A note will be added to
            the investigation log recording the recipient and timestamp.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={isSending}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!recipientEmail.trim() || isSending}
            className="gap-1.5"
          >
            <Mail className="h-3.5 w-3.5" />
            {isSending ? "Sending…" : "Send report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function buildPrintHtml(
  report: SafetyReport,
  notes: SafetyReportNote[],
  statusHistory: SafetyReportStatusHistoryEntry[],
): string {
  const esc = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

  const fmt = (iso: string) => new Date(iso).toLocaleString("en-GB");

  const statusRows = statusHistory.length === 0
    ? `<tr><td colspan="4" class="empty">No status transitions recorded.</td></tr>`
    : statusHistory
        .map(
          (e, i) => `
      <tr class="${i % 2 === 0 ? "row-even" : "row-odd"}">
        <td class="num">#${i + 1}</td>
        <td>${esc(STATUS_LABELS[e.previousStatus] ?? e.previousStatus)} &rarr; ${esc(STATUS_LABELS[e.newStatus] ?? e.newStatus)}</td>
        <td>${esc(e.actorDisplayName)}</td>
        <td class="date">${esc(fmt(e.createdAt))}</td>
      </tr>`,
        )
        .join("");

  const noteBlocks = notes.length === 0
    ? `<p class="empty">No investigation notes recorded.</p>`
    : notes
        .map(
          (n) => `
      <div class="note">
        <div class="note-meta">
          <span class="note-author">${esc(n.actorDisplayName)}</span>
          <span class="note-date">${esc(fmt(n.createdAt))}</span>
        </div>
        <p class="note-body">${esc(n.note)}</p>
      </div>`,
        )
        .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Safety Report ${esc(report.referenceNumber)} — RheumHub</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #1e293b; background: #fff; }

    /* Screen layout */
    .page { max-width: 860px; margin: 0 auto; padding: 32px 24px; }

    /* Print button — hidden when printing */
    .print-bar { display: flex; justify-content: flex-end; margin-bottom: 24px; }
    .print-btn {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 8px 16px; background: #0d6760; color: #fff;
      border: none; border-radius: 6px; font-size: 13px; font-weight: 600;
      cursor: pointer; line-height: 1;
    }
    .print-btn:hover { background: #0a5550; }

    /* Header banner */
    .header {
      background: #0d6760; color: #fff;
      padding: 14px 20px;
      border-radius: 8px 8px 0 0;
      display: flex; justify-content: space-between; align-items: center;
    }
    .header-title { font-size: 15px; font-weight: 700; }
    .header-sub { font-size: 10px; opacity: 0.85; }

    .body { border: 1px solid #cbd5e1; border-top: none; border-radius: 0 0 8px 8px; padding: 24px 20px; }

    .ref { font-size: 18px; font-weight: 700; margin-bottom: 4px; }
    .generated { font-size: 10px; color: #94a3b8; font-style: italic; margin-bottom: 16px; }

    hr { border: none; border-top: 1px solid #cbd5e1; margin: 16px 0; }

    .section-title { font-size: 13px; font-weight: 700; margin-bottom: 10px; }

    .fields { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; margin-bottom: 4px; }
    .field-label { font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em; }
    .field-value { font-size: 12px; color: #1e293b; margin-top: 2px; }
    .field-mono { font-family: monospace; font-size: 11px; }

    .desc-box {
      background: #f8fafc; border: 1px solid #cbd5e1; border-radius: 6px;
      padding: 12px 14px; font-size: 12px; white-space: pre-wrap; line-height: 1.6;
    }

    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th { text-align: left; padding: 6px 8px; background: #f1f5f9; color: #475569; font-weight: 700; font-size: 10px; text-transform: uppercase; }
    td { padding: 6px 8px; vertical-align: top; }
    td.num { color: #94a3b8; font-weight: 700; width: 30px; }
    td.date { color: #94a3b8; white-space: nowrap; }
    .row-even td { background: #f8fafc; }
    .row-odd td { background: #f1f5f9; }
    td.empty, p.empty { color: #94a3b8; font-style: italic; padding: 8px 0; }

    .note { background: #eff6ff; border: 1px solid #bae6fd; border-radius: 6px; padding: 10px 12px; margin-bottom: 8px; }
    .note-meta { display: flex; justify-content: space-between; margin-bottom: 6px; }
    .note-author { font-weight: 700; font-size: 11px; color: #475569; }
    .note-date { font-size: 10px; color: #94a3b8; }
    .note-body { font-size: 12px; color: #1e293b; white-space: pre-wrap; line-height: 1.6; }

    .footer { margin-top: 20px; font-size: 10px; color: #94a3b8; font-style: italic; }

    @media print {
      body { font-size: 11px; }
      .print-bar { display: none !important; }
      .page { padding: 0; max-width: none; }
      .header { border-radius: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .body { border: none; border-radius: 0; }
      .row-even td, .row-odd td { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .note { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      a { text-decoration: none; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="print-bar">
      <button class="print-btn" onclick="window.print()">&#x1F5A8; Print / Save as PDF</button>
    </div>

    <div class="header">
      <span class="header-title">RheumHub &mdash; Safety Report</span>
      <span class="header-sub">DCB0129 Clinical Safety Incident Record</span>
    </div>

    <div class="body">
      <div class="ref">${esc(report.referenceNumber)}</div>
      <div class="generated">Generated: ${esc(new Date().toLocaleString("en-GB"))}</div>

      <hr />

      <div class="section-title">Report Details</div>
      <div class="fields">
        <div>
          <div class="field-label">Reference Number</div>
          <div class="field-value field-mono">${esc(report.referenceNumber)}</div>
        </div>
        <div>
          <div class="field-label">Concern Type</div>
          <div class="field-value">${esc(CONCERN_LABELS[report.concernType] ?? report.concernType)}</div>
        </div>
        <div>
          <div class="field-label">Submitter Role</div>
          <div class="field-value">${esc(report.submitterRole ? (ROLE_LABELS[report.submitterRole] ?? report.submitterRole) : "Unknown")}</div>
        </div>
        <div>
          <div class="field-label">Submitter User ID</div>
          <div class="field-value field-mono">${esc(report.userId)}</div>
        </div>
        <div>
          <div class="field-label">Page / Feature</div>
          <div class="field-value">${report.pageOrFeature ? esc(report.pageOrFeature) : "<em style='color:#94a3b8'>Not specified</em>"}</div>
        </div>
        <div>
          <div class="field-label">Submitted</div>
          <div class="field-value">${esc(fmt(report.createdAt))}</div>
        </div>
        <div>
          <div class="field-label">Current Status</div>
          <div class="field-value">${esc(STATUS_LABELS[report.status] ?? report.status)}</div>
        </div>
      </div>

      <hr />

      <div class="section-title">Full Description</div>
      <div class="desc-box">${esc(report.description)}</div>

      <hr />

      <div class="section-title">Status History (${statusHistory.length} transition${statusHistory.length !== 1 ? "s" : ""})</div>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Transition</th>
            <th>Changed by</th>
            <th>Date &amp; Time</th>
          </tr>
        </thead>
        <tbody>${statusRows}</tbody>
      </table>

      <hr />

      <div class="section-title">Investigation Notes (${notes.length})</div>
      ${noteBlocks}

      <hr />

      <div class="footer">
        Exported from RheumHub on ${esc(new Date().toLocaleString("en-GB"))} &middot;
        Confidential &mdash; DCB0129 Clinical Safety Document
      </div>
    </div>
  </div>
</body>
</html>`;
}

function ReportRow({
  report,
  onUpdateStatus,
  isUpdating,
}: {
  report: SafetyReport;
  onUpdateStatus: (id: number, status: ReportStatus) => void;
  isUpdating: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const { toast } = useToast();

  const handleExportPdf = async () => {
    setIsExporting(true);
    try {
      const [notes, statusHistory] = await Promise.all([
        apiFetch(`/api/admin/safety-reports/${report.id}/notes`) as Promise<SafetyReportNote[]>,
        apiFetch(`/api/admin/safety-reports/${report.id}/status-history`) as Promise<SafetyReportStatusHistoryEntry[]>,
      ]);
      generateSafetyReportPdf(report, notes ?? [], statusHistory ?? []);
    } catch (e) {
      toast({
        title: "Export failed",
        description: e instanceof Error ? e.message : "Could not generate PDF.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handlePrint = async () => {
    const tab = window.open("", "_blank");
    if (!tab) {
      toast({
        title: "Pop-up blocked",
        description: "Please allow pop-ups for this site and try again.",
        variant: "destructive",
      });
      return;
    }
    tab.document.write(
      `<!DOCTYPE html><html><head><title>Loading…</title></head><body style="font-family:Arial,sans-serif;padding:40px;color:#475569">Loading report…</body></html>`,
    );
    tab.document.close();

    setIsPrinting(true);
    try {
      const [notes, statusHistory] = await Promise.all([
        apiFetch(`/api/admin/safety-reports/${report.id}/notes`) as Promise<SafetyReportNote[]>,
        apiFetch(`/api/admin/safety-reports/${report.id}/status-history`) as Promise<SafetyReportStatusHistoryEntry[]>,
      ]);
      const html = buildPrintHtml(report, notes ?? [], statusHistory ?? []);
      tab.document.open();
      tab.document.write(html);
      tab.document.close();
    } catch (e) {
      tab.close();
      toast({
        title: "Print failed",
        description: e instanceof Error ? e.message : "Could not load report data.",
        variant: "destructive",
      });
    } finally {
      setIsPrinting(false);
    }
  };

  const currentIdx = STATUS_FLOW.indexOf(report.status as ReportStatus);
  const nextStatus = currentIdx >= 0 && currentIdx < STATUS_FLOW.length - 1
    ? STATUS_FLOW[currentIdx + 1]
    : null;

  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <button
        onClick={() => setExpanded((p) => !p)}
        className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs font-mono font-semibold text-slate-600">{report.referenceNumber}</span>
            <Badge variant="outline" className={`text-xs ${CONCERN_COLOURS[report.concernType] ?? ""}`}>
              {CONCERN_LABELS[report.concernType] ?? report.concernType}
            </Badge>
            <RoleBadge role={report.submitterRole} />
            <StatusBadge status={report.status} />
          </div>
          <p className="text-xs text-slate-500 truncate">{report.description}</p>
        </div>
        <div className="text-xs text-slate-400 shrink-0 hidden sm:block whitespace-nowrap">
          {new Date(report.createdAt).toLocaleDateString("en-GB")}
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-5 py-4 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs font-medium text-slate-500 mb-0.5">Reference number</p>
              <p className="text-slate-800 font-mono text-xs">{report.referenceNumber}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 mb-0.5">Submitted by (User ID)</p>
              <p className="text-slate-800 font-mono text-xs break-all">{report.userId}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 mb-0.5">Submitter role</p>
              <RoleBadge role={report.submitterRole} />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 mb-0.5">Report type</p>
              <p className="text-slate-800">{CONCERN_LABELS[report.concernType] ?? report.concernType}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 mb-0.5">Page / Feature</p>
              <p className="text-slate-800">
                {report.pageOrFeature ?? <span className="text-slate-400 italic">Not specified</span>}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 mb-0.5">Submitted</p>
              <p className="text-slate-800 text-xs">
                {new Date(report.createdAt).toLocaleString("en-GB")}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 mb-0.5">Current status</p>
              <StatusBadge status={report.status} />
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-slate-500 mb-1">Full description</p>
            <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 whitespace-pre-wrap">
              {report.description}
            </p>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-xs font-medium text-slate-600">Update status</p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportPdf}
                  disabled={isExporting}
                  className="gap-1.5 h-7 text-xs text-slate-600"
                >
                  <FileDown className="h-3.5 w-3.5" />
                  {isExporting ? "Generating…" : "Export PDF"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrint}
                  disabled={isPrinting}
                  className="gap-1.5 h-7 text-xs text-slate-600"
                >
                  <Printer className="h-3.5 w-3.5" />
                  {isPrinting ? "Loading…" : "Print"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEmailDialogOpen(true)}
                  className="gap-1.5 h-7 text-xs text-slate-600"
                >
                  <Mail className="h-3.5 w-3.5" />
                  Email report
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {STATUS_FLOW.map((s) => (
                <button
                  key={s}
                  disabled={isUpdating || report.status === s}
                  onClick={() => onUpdateStatus(report.id, s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    report.status === s
                      ? `${STATUS_COLOURS[s]} cursor-default`
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300"
                  }`}
                >
                  {report.status === s && <span className="mr-1">✓</span>}
                  {STATUS_LABELS[s]}
                </button>
              ))}
            </div>
            {nextStatus && (
              <p className="text-xs text-slate-400 mt-2">
                Suggested next step: <strong>{STATUS_LABELS[nextStatus]}</strong>
              </p>
            )}
          </div>

          <NotesSection reportId={report.id} />
        </div>
      )}

      <EmailReportDialog
        report={report}
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
      />
    </div>
  );
}

export default function AdminSafetyReports() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [typeFilter, setTypeFilter] = useState<ConcernType>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [isBulkExportingPdf, setIsBulkExportingPdf] = useState(false);
  const [isBulkExportingCsv, setIsBulkExportingCsv] = useState(false);

  const queryKey = ["admin", "safety-reports", typeFilter, statusFilter, roleFilter];

  const { data: reports = [], isLoading, error, refetch } = useQuery<SafetyReport[]>({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.set("concernType", typeFilter);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (roleFilter !== "all") params.set("submitterRole", roleFilter);
      const qs = params.toString();
      return apiFetch(`/api/admin/safety-reports${qs ? `?${qs}` : ""}`) as Promise<SafetyReport[]>;
    },
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: ReportStatus }) =>
      apiFetch(`/api/admin/safety-reports/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      }),
    onMutate: ({ id }) => setUpdatingId(id),
    onSuccess: (_, { status }) => {
      qc.invalidateQueries({ queryKey: ["admin", "safety-reports"] });
      toast({ title: `Status updated to "${STATUS_LABELS[status]}"` });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
    onSettled: () => setUpdatingId(null),
  });

  const pendingCount = reports.filter((r) => r.status === "pending").length;

  const buildFilterDescription = () => {
    const parts: string[] = [];
    if (typeFilter !== "all") parts.push(CONCERN_LABELS[typeFilter] ?? typeFilter);
    if (statusFilter !== "all") parts.push(STATUS_LABELS[statusFilter] ?? statusFilter);
    if (roleFilter !== "all") parts.push(ROLE_LABELS[roleFilter] ?? roleFilter);
    return parts.join(", ");
  };

  const handleExportCsv = () => {
    if (reports.length === 0) return;
    setIsBulkExportingCsv(true);
    try {
      exportCsv(reports, buildFilterDescription());
    } finally {
      setIsBulkExportingCsv(false);
    }
  };

  const handleBulkExportPdf = async () => {
    if (reports.length === 0) return;
    setIsBulkExportingPdf(true);
    try {
      const items = await Promise.all(
        reports.map(async (report) => {
          const [notes, statusHistory] = await Promise.all([
            apiFetch(`/api/admin/safety-reports/${report.id}/notes`) as Promise<SafetyReportNote[]>,
            apiFetch(`/api/admin/safety-reports/${report.id}/status-history`) as Promise<SafetyReportStatusHistoryEntry[]>,
          ]);
          return { report, notes: notes ?? [], statusHistory: statusHistory ?? [] };
        }),
      );
      generateBulkPdf(items, buildFilterDescription());
    } catch (e) {
      toast({
        title: "Export failed",
        description: e instanceof Error ? e.message : "Could not generate PDF.",
        variant: "destructive",
      });
    } finally {
      setIsBulkExportingPdf(false);
    }
  };

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Safety Reports"
        description="Review and manage HCP safety concern and bug reports (DCB0129 incident reporting)"
        action={
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              disabled={isBulkExportingCsv || reports.length === 0 || isLoading}
              className="gap-1.5"
            >
              <FileDown className="h-4 w-4" />
              {isBulkExportingCsv ? "Exporting…" : "Export all (CSV)"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkExportPdf}
              disabled={isBulkExportingPdf || reports.length === 0 || isLoading}
              className="gap-1.5"
            >
              <FileDown className="h-4 w-4" />
              {isBulkExportingPdf ? "Fetching data…" : "Export all (PDF)"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-1.5">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        }
      />

      {pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 mb-5 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
          <p>
            <strong>{pendingCount}</strong> report{pendingCount !== 1 ? "s" : ""} awaiting review. Update their status as you investigate.
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Type</span>
          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as ConcernType)}>
            <SelectTrigger className="w-48 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="clinical-content-error">Clinical content error</SelectItem>
              <SelectItem value="ai-response-concern">AI response concern</SelectItem>
              <SelectItem value="data-issue">Data issue</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Role</span>
          <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v as RoleFilter)}>
            <SelectTrigger className="w-44 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="nurse">Nurse</SelectItem>
              <SelectItem value="doctor">Doctor</SelectItem>
              <SelectItem value="pharmacist">Pharmacist</SelectItem>
              <SelectItem value="physio">Physiotherapist</SelectItem>
              <SelectItem value="ot">Occupational Therapist</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Status</span>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="w-40 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="investigating">Investigating</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-slate-400">
          <AlertTriangle className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">Loading reports…</p>
        </div>
      ) : error ? (
        <div className="text-center py-16 text-slate-500">
          <AlertTriangle className="h-8 w-8 mx-auto mb-3 text-amber-400" />
          <p className="font-medium">Failed to load safety reports</p>
          <p className="text-sm mt-1">Please refresh and try again.</p>
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <AlertTriangle className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <p className="font-medium text-slate-600">No safety reports found</p>
          <p className="text-sm mt-1">
            {typeFilter !== "all" || statusFilter !== "all" || roleFilter !== "all"
              ? "Try adjusting your filters."
              : "No reports have been submitted yet."}
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-3">
            {reports.length} report{reports.length !== 1 ? "s" : ""} shown
          </p>
          <div className="space-y-3">
            {reports.map((report) => (
              <ReportRow
                key={report.id}
                report={report}
                onUpdateStatus={(id, status) => updateStatus.mutate({ id, status })}
                isUpdating={updatingId === report.id}
              />
            ))}
          </div>
        </>
      )}
    </AdminLayout>
  );
}
